import {
	Grid,
	Paper,
	useTheme,
	IconButton,
	Pagination,
	Drawer,
	ClickAwayListener,
	FormControlLabel,
	FormGroup,
	Switch,
	Typography,
	Button,
} from "@mui/material";
import useStyles from "./styles";
import api from "../../../../services/api";
import { findManyOpcaoCircuito } from "./circuito/opcaoCircuitoService";
import { findManyTagsCircuito } from "./circuito/tagsCircuitoService";
import { flattenHostsByService } from "../../../../utils/hostTypes";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Cancel, CheckCircle, CloseOutlined, UnfoldMore, UnfoldLess } from "@mui/icons-material";
import { useBlocker, useOutletContext } from "react-router-dom";
import { Box } from "@mui/system";
import { v4 as uuidv4 } from "uuid";
import LoaderThreeDots from "../../../../components/loadingAnimation";
import { ServiceItem } from "./serviceItem";
import InternoModalConfirmacaoSaida from "../../../../components/modalSaindoDaPagina";
import CardSelection from "./cardSelection";
import InformationMale from "../../../../components/artsPersonComponent/informationMale";
import AplicarEmMassaContext from "./aplicarEmMassaContext";
import { applyManyDiagramaTemplate } from "./circuito/diagramaEquipamentos/templates/templatesService";
import isCircuitoConfigurado from "./isCircuitoConfigurado";

export default function FormCircuito() {
	const {
		setSnackbar,
		selectedCliente: clienteInfo,
		changed,
		searchTerm,
		setSearchTerm,
		setChanged,
		services,
		setServices,
		setValue,
		selectedService,
		showCanceled,
		setShowCanceled,
		urlServiceId,
		configStatusFilter,
	} = useOutletContext();

	const [editMode, setEditMode] = useState(false);
	const [selectCliente, setSelectCliente] = useState("");
	const [selectedTitle, setSelectedTitle] = useState("");
	// Circuito selecionado dentro do serviço aberto (chave = id ou tempId)
	const [selectedCircuitKey, setSelectedCircuitKey] = useState(null);
	const getCircuitKey = (circuit) => circuit?.id || circuit?.tempId;
	// Sinal dedicado de reinicialização do diagrama por aplicação em massa
	const [reapplySignal, setReapplySignal] = useState(0);
	// Ref sincronizado com selectedCircuitKey para leitura estável dentro de callbacks
	const selectedCircuitKeyRef = useRef(selectedCircuitKey);
	useEffect(() => {
		selectedCircuitKeyRef.current = selectedCircuitKey;
	}, [selectedCircuitKey]);
	// Remoção de circuito: estado do modal de confirmação, acionado pelos subitens do CardSelection
	const [confirmRemoveCircuit, setConfirmRemoveCircuit] = useState(null);
	const [loadingRemoveCircuit, setLoadingRemoveCircuit] = useState(false);
	const [deletingCircuitIndex, setDeletingCircuitIndex] = useState(null);

	// Clicar num circuito na lista lateral é local à página: não deve alterar a
	// URL nem o filtro da barra superior. O :serviceId na URL só é adicionado ao
	// selecionar um serviço no filtro, e removido ao limpá-lo (handleSetSelectedService
	// em 2.0-clientes/index.js).
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [openDrawer, setOpenDrawer] = useState(false);

	// resize listener com cleanup
	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	///////////////////////////////////////////////////////////////

	const [confirmacaoSaidaPagina, setConfirmacaoSaidaPagina] = useState(false);
	const pendingNavigation = useRef(null);
	// Loading do circuito atualmente aberto (reportado por serviceItem.js) — usado só para não
	// desmontar o circuito com um handleSave em voo ao confirmar saída (ver handleConfirmNavigation)
	const [circuitoSalvando, setCircuitoSalvando] = useState(false);

	const toggleDrawer = (newOpen) => {
		setOpenDrawer(newOpen);
	};

	// Bloqueia a navegação se houver alterações
	const blocker = useBlocker(({ currentLocation, nextLocation }) => {
		return currentLocation.pathname !== nextLocation.pathname && changed;
	});

	// Mostra modal quando bloquear e marca que é navegação de rota
	useEffect(() => {
		if (blocker?.state === "blocked") {
			pendingNavigation.current = { type: "route" };
			setConfirmacaoSaidaPagina(true);
		}
	}, [blocker?.state]);

	// Confirmar saída
	const handleConfirmNavigation = () => {
		// Guarda extra: o botão do modal já fica desabilitado (confirmDisabled), isso é
		// só defesa contra chamar execute() por outro caminho com um save ainda em voo
		if (circuitoSalvando) return;
		setEditMode(false);
		setConfirmacaoSaidaPagina(false);
		setChanged(false);

		// Se for navegação de rota, libera o bloqueio
		if (pendingNavigation.current?.type === "route") {
			blocker?.proceed?.();
			pendingNavigation.current = null;
			return;
		}

		// Se for troca de serviço/circuito (ação interna), executa agora
		if (pendingNavigation.current?.type === "accordion") {
			const { serviceId, circuitKey } = pendingNavigation.current.to || {};
			pendingNavigation.current = null;
			setSelectCliente(serviceId);
			setSelectedCircuitKey(circuitKey);
			if (windowWidth < 750) toggleDrawer(true);
			return;
		}

		// Se for fechar o Drawer, fecha agora
		if (pendingNavigation.current?.type === "close-drawer") {
			pendingNavigation.current = null;
			toggleDrawer(false);
			return;
		}

		// Se for adicionar circuito (ação interna), cria e seleciona agora, forçando edição
		if (pendingNavigation.current?.type === "add-circuito") {
			const { serviceId, serviceNome, novoCircuito } = pendingNavigation.current.to || {};
			pendingNavigation.current = null;
			setForms((prev) => ({
				...prev,
				[serviceId]: {
					...prev?.[serviceId],
					clientServiceId: serviceId,
					circuits: [...(prev?.[serviceId]?.circuits || []), novoCircuito],
				},
			}));
			if (serviceNome) setSelectedTitle(`${serviceId} - ${serviceNome}`);
			setSelectCliente(serviceId);
			setSelectedCircuitKey(novoCircuito.tempId);
			setEditMode(true);
			setChanged(true);
			if (windowWidth < 750) toggleDrawer(true);
			return;
		}
	};

	// Cancelar saída
	const handleCancelNavigation = () => {
		setConfirmacaoSaidaPagina(false);
		setValue(2); // Seleciona a aba de circuitos
		if (blocker?.reset) {
			blocker?.reset(); // Cancela a navegação pendente
		}
		pendingNavigation.current = null;
	};

	// Bloquear recarregar página, fechar aba, etc.
	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (changed) {
				e.preventDefault();
				e.returnValue = ""; // Requerido por todos os browsers modernos
			}
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [changed]);

	// Confirmar clique em inputs fora da aba
	const pendingInputClickRef = useRef(null);
	const [confirmInputClick, setConfirmInputClick] = useState(false);

	const handleConfirmPendingClick = () => {
		setConfirmInputClick(false);
		setChanged(false);

		if (pendingInputClickRef.current) {
			pendingInputClickRef.current();
			pendingInputClickRef.current = null;
		}
	};

	const handleCancelPendingClick = () => {
		setConfirmInputClick(false);
		pendingInputClickRef.current = null;
	};

	// ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
	////////////////////////////////////////////////////////////

	const clienteId = clienteInfo?.id || "";
	const theme = useTheme();
	const styles = useStyles(theme);
	const [loading, setLoading] = useState(false);
	const [otherOptions, setOtherOptions] = useState({});
	const [optionsRoteamento, setOptionsRoteamento] = useState([]);
	const [optionsEntregaFinal, setOptionsEntregaFinal] = useState([]);
	const [optionsEntregaFinalCliente, setOptionsEntregaFinalCliente] = useState(
		[],
	);
	// Tags POP/Regional são globais (não por circuito) — buscadas uma vez aqui e
	// passadas via props, em vez de cada InternoCircuitoCliente buscar a própria cópia
	const [popTags, setPopTags] = useState([]);
	const [regionalTags, setRegionalTags] = useState([]);
	const [loadingTags, setLoadingTags] = useState(false);
	const [forms, setForms] = useState([]);

	// Sincroniza o circuito selecionado quando o filtro da barra superior muda ou via URL
	useEffect(() => {
		const targetSid = selectedService?.id_cliente_servico ?? selectedService?.id ?? urlServiceId;
		if (targetSid && String(targetSid) !== String(selectCliente)) {
			setSelectCliente(targetSid);
			const circuitsDoAlvo = forms?.[targetSid]?.circuits;
			setSelectedCircuitKey(circuitsDoAlvo?.[0] ? getCircuitKey(circuitsDoAlvo[0]) : null);
		} else if (!targetSid && selectCliente) {
			setSelectCliente(null);
			setSelectedCircuitKey(null);
		}
	}, [selectedService, urlServiceId]);

	// Garante circuito selecionado válido quando o serviço aberto tiver algum (forms tardios ou seleção órfã)
	useEffect(() => {
		const circuitsDoServico = forms?.[selectCliente]?.circuits;
		if (!circuitsDoServico?.length) {
			if (selectedCircuitKey !== null) setSelectedCircuitKey(null);
			return;
		}
		const pertenceAoServico = circuitsDoServico.some(
			(circuit) => getCircuitKey(circuit) === selectedCircuitKey,
		);
		if (!pertenceAoServico) {
			setSelectedCircuitKey(getCircuitKey(circuitsDoServico[0]));
		}
	}, [selectCliente, forms]);

	const [clientCpes, setClientCpes] = useState({});
	// Hosts do cliente agregados das 4 categorias (CPE/ONU/Link/Sessão), por serviço —
	// usado pelo combobox de host do diagrama de equipamentos
	const [clientHosts, setClientHosts] = useState({});

	// Estados para paginação e accordion
	const [currentPage, setCurrentPage] = useState(1);
	const [expandedAccordion, setExpandedAccordion] = useState(null);
	const itemsPerPage = windowWidth < 2500 ? 8 : 11;

	useEffect(() => {
		getServicesAndForms();
		setSelectCliente(null);
		setSelectedCircuitKey(null);
		setSearchTerm("");
	}, [clienteInfo]);

	// Resetar para a primeira página quando os filtros mudarem
	useEffect(() => {
		setCurrentPage(1);
	}, [searchTerm, selectedService, showCanceled]);

	// Fechar accordion quando mudar de página
	useEffect(() => {
		setExpandedAccordion(null);
	}, [currentPage]);

	async function getServicesAndForms() {
		try {
			setLoading(true);
			setServices([]);
			setForms([]);
			const forms = await api.post("/interno/find/circuitForms", {
				clienteId: clienteId,
			});

			setForms(forms?.data || []);

			// Busca Hosts CPEs do cliente de uma vez
			try {
				const cpesRes = await api.post("/interno/findMany/hostsCliente", {
					clienteId: clienteId,
					includeEmptyHosts: true,
					mapHostsByServiceAndEquipmentType: true,
				});
				setClientCpes(cpesRes?.data?.cpes || {});
				setClientHosts(flattenHostsByService(cpesRes?.data || {}));
			} catch (e) {
				console.error("Falha ao buscar hostsCPE:", e);
				setClientCpes({});
				setClientHosts({});
			}

			// Opções de manipulação + tags POP/Regional (globais, uma busca só para
			// todos os circuitos do serviço, em vez de cada circuito buscar a própria cópia)
			setLoadingTags(true);
			const [opcoes, popTagsRes, regionalTagsRes] = await Promise.all([
				findManyOpcaoCircuito(),
				findManyTagsCircuito("pop"),
				findManyTagsCircuito("regional"),
			]).finally(() => setLoadingTags(false));
			setOtherOptions({
				serviceTypes: opcoes?.serviceTypes,
				deliveryMedium: opcoes?.deliveryMedium,
			});
			setOptionsRoteamento(opcoes?.equipRouter || []);
			setOptionsEntregaFinal(opcoes?.equipDelivery || []);
			setOptionsEntregaFinalCliente(opcoes?.equipLastMile || []);
			setPopTags(popTagsRes || []);
			setRegionalTags(regionalTagsRes || []);
			// Serviços
			let services = [];

			// O backend acha o cliente pelo clienteId e consulta o ERP dele.
			services = await api.post("/interno/find/allServiceClient", {
				cnpj: clienteInfo?.cnpj,
				idClienteErp: clienteInfo?.clienteErpId,
				clienteId: clienteInfo?.id,
				includeInactive: true,
			});

			setServices(services?.data || []);
		} catch (error) {
			setForms([]);
			setServices([]);
			console.error(error);
			setSnackbar({
				children: "Error: Falha ao obter serviços",
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	}
	async function getDeliveryOptions() {
		try {
			const opcoes = await findManyOpcaoCircuito();
			setOtherOptions({
				serviceTypes: opcoes?.serviceTypes,
				deliveryMedium: opcoes?.deliveryMedium,
			});
		} catch (error) {
			console.error(error);
			setSnackbar({
				children: "Error: Falha ao obter opções",
				severity: "error",
			});
		}
	}

	const filteredServices = useMemo(() => {
		const selectedServiceId =
			selectedService?.id_cliente_servico ?? selectedService?.id;
		const hasSelectedService =
			selectedServiceId !== undefined &&
			selectedServiceId !== null &&
			selectedServiceId !== "";

		// Uma seleção explícita na barra superior deve mostrar sempre esse
		// serviço, ignorando "Mostrar Cancelados" / "Configuração" — senão a
		// tela mostra "nenhum circuito encontrado" mesmo com o serviço existindo.
		if (hasSelectedService) {
			return (
				services?.filter(
					(service) =>
						String(service?.id_cliente_servico ?? service?.id) ===
						String(selectedServiceId),
				) || []
			);
		}

		let visibleServices = services || [];

		if (!showCanceled) {
			visibleServices = visibleServices?.filter(
				(service) =>
					!String(service?.status || "")
						.toLowerCase()
						.includes("cancelado"),
			);
		}

		if (configStatusFilter === "configurado") {
			visibleServices = visibleServices?.filter((servico) => {
				const serviceForms = forms?.[servico?.id_cliente_servico]?.circuits;
				const isConfigured = serviceForms?.some((circuit) => {
					if (circuit?.syncedByErp) return false;
					return (
						!!circuit?.serial ||
						!!circuit?.blockIP ||
						!!circuit?.ipPublico ||
						!!circuit?.vlanId ||
						!!circuit?.companySideIPv4 ||
						!!circuit?.companySideIPv6 ||
						!!circuit?.clientIPv4 ||
						!!circuit?.clientIPv6 ||
						!!circuit?.observations ||
						!!circuit?.lastMilePartner?.partnerName ||
						!!circuit?.lastMilePartner?.partnerContact ||
						(circuit?.lastMilePartner?.partnerEnabled !== null && circuit?.lastMilePartner?.partnerEnabled !== undefined) ||
						(circuit?.endClient?.endClientEnabled !== null && circuit?.endClient?.endClientEnabled !== undefined) ||
						(circuit?.speed?.download !== null && circuit?.speed?.download !== undefined) ||
						(circuit?.speed?.upload !== null && circuit?.speed?.upload !== undefined)
					);
				}) || false;
				return isConfigured;
			});
		} else if (configStatusFilter === "nao_configurado") {
			visibleServices = visibleServices?.filter((servico) => {
				const serviceForms = forms?.[servico?.id_cliente_servico]?.circuits;
				const isConfigured = serviceForms?.some((circuit) => {
					if (circuit?.syncedByErp) return false;
					return (
						!!circuit?.serial ||
						!!circuit?.blockIP ||
						!!circuit?.ipPublico ||
						!!circuit?.vlanId ||
						!!circuit?.companySideIPv4 ||
						!!circuit?.companySideIPv6 ||
						!!circuit?.clientIPv4 ||
						!!circuit?.clientIPv6 ||
						!!circuit?.observations ||
						!!circuit?.lastMilePartner?.partnerName ||
						!!circuit?.lastMilePartner?.partnerContact ||
						(circuit?.lastMilePartner?.partnerEnabled !== null && circuit?.lastMilePartner?.partnerEnabled !== undefined) ||
						(circuit?.endClient?.endClientEnabled !== null && circuit?.endClient?.endClientEnabled !== undefined) ||
						(circuit?.speed?.download !== null && circuit?.speed?.download !== undefined) ||
						(circuit?.speed?.upload !== null && circuit?.speed?.upload !== undefined)
					);
				}) || false;
				return !isConfigured;
			});
		}

		if (searchTerm === "") {
			return visibleServices;
		}

		return visibleServices?.filter((service) => {
			if (searchTerm !== "") {
				const searchLower = searchTerm?.toLowerCase()?.trim();
				const searchableValues = [
					service?.id_cliente_servico,
					service?.nome,
					service?.status,
					service?.endereco_instalacao?.completo,
					service?.ownerNome,
				];

				return searchableValues?.some(
					(value) =>
						value && value.toString().toLowerCase().includes(searchLower),
				);
			}
			return true;
		});
	}, [services, searchTerm, selectedService, showCanceled, configStatusFilter, forms]);

	const currentServices = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return filteredServices?.slice(startIndex, endIndex) || [];
	}, [filteredServices, currentPage, itemsPerPage]);

	const totalPages = useMemo(() => {
		return Math.ceil(filteredServices?.length / itemsPerPage);
	}, [filteredServices, itemsPerPage]);

	// Em grupos econômicos, resolve o cliente membro dono do serviço selecionado
	// para rotear a escrita do circuito ao cliente correto
	const selectedOwnerId = useMemo(() => {
		const servico = services?.find(
			(service) =>
				String(service?.id_cliente_servico) === String(selectCliente),
		);
		return servico?.ownerClienteId || null;
	}, [services, selectCliente]);

	// Árvore serviço -> circuito para o modal de aplicar template em massa (Task 4), entregue via
	// AplicarEmMassaContext para não enfiar prop por circuito/diagramaEquipamentos/DiagramaCanvas.
	// clienteId de cada serviço já sai resolvido pela regra I6 (ownerClienteId || clienteInfo?.id)
	// — o modal só consome, nunca recalcula. Título no mesmo formato da lista esquerda
	// (cardSelection.js: `${id} - ${nome}`, sem prefixo de labelErp — ruling do controller).
	const servicosParaAplicarEmMassa = useMemo(() => {
		// Mesmo critério de "Mostrar Cancelados" da lista de serviços (filteredServices acima):
		// por padrão só serviços ativos entram na árvore do modal, a não ser que o toggle esteja ligado
		const servicosVisiveis = showCanceled
			? services || []
			: (services || []).filter(
					(service) =>
						!String(service?.status || "")
							.toLowerCase()
							.includes("cancelado"),
				);

		return servicosVisiveis.map((service) => {
			const titulo = service?.nome
				? `${service?.id_cliente_servico} - ${service?.nome}`
				: `${service?.id_cliente_servico}`;
			return {
				clientServiceId: service?.id_cliente_servico,
				titulo,
				clienteId: service?.ownerClienteId || clienteInfo?.id,
				circuits: forms?.[service?.id_cliente_servico]?.circuits || [],
			};
		});
	}, [services, forms, clienteInfo, showCanceled]);

	const handlePageChange = useCallback((event, value) => {
		setCurrentPage(value);
	}, []);

	const updateForm = useCallback((serviceId, updatedForm, tempId) => {
		setForms((prev) => {
			// Clona o estado atual
			const newForms = { ...prev };

			// Atualiza o circuito salvo
			const updatedForms = newForms[serviceId]?.circuits?.map((circuit) =>
				// Se o circuito tinha um tempId igual ao salvo, substitui. O `tempId &&`
				// é obrigatório: circuito já existente nunca tem tempId (fica undefined),
				// e sem essa guarda `circuit?.tempId === tempId` virava `undefined ===
				// undefined` (true) para QUALQUER outro circuito já existente do mesmo
				// serviço — ao salvar 2+ circuitos existentes juntos (batch pré-Task 7), o
				// último onFormUpdate a rodar sobrescrevia todos eles com o resultado de
				// só um (bug visual: um circuito "duplicava" o outro até dar F5).
				(tempId && circuit?.tempId === tempId) || circuit?.id === updatedForm?.id
					? { ...updatedForm, tempId: tempId }
					: circuit,
			);

			// Atualiza a lista de circuitos do serviço
			newForms[serviceId] = {
				circuits: updatedForms,
			};

			return newForms;
		});
	}, []);

	// Substitui no mapa forms os circuitos afetados pela aplicação em massa, casando por id,
	// e acrescenta os que foram criados no serviço correspondente
	const reidratarCircuitosAplicados = useCallback((aplicados = []) => {
		// Incrementa sinal dedicado quando o circuito aberto está entre os alvos da aplicação em massa
		const circuitoAbertoAfetado = (aplicados || []).some(
			(c) => c?.id && c?.id === selectedCircuitKeyRef?.current,
		);
		if (circuitoAbertoAfetado) {
			setReapplySignal((prev) => prev + 1);
		}

		setForms((prev) => {
			const newForms = { ...prev };

			// Agrupa os circuitos devolvidos por serviço: um template pode ser aplicado a
			// circuitos de mais de um serviço numa única chamada
			const servicoIds = (aplicados || [])
				.map((circuito) => circuito?.clientServiceId)
				.filter((serviceId, index, lista) => serviceId && lista.indexOf(serviceId) === index);

			servicoIds.forEach((serviceId) => {
				const circuitosDoServico = (aplicados || []).filter(
					(circuito) => circuito?.clientServiceId === serviceId,
				);
				const circuitsAtuais = newForms[serviceId]?.circuits || [];
				const idsAtuais = circuitsAtuais.map((circuit) => circuit?.id);

				// Substitui, casando por id, os circuitos que já existiam no serviço
				const circuitsAtualizados = circuitsAtuais.map((circuit) => {
					const aplicado = circuitosDoServico.find((c) => c?.id === circuit?.id);
					return aplicado || circuit;
				});

				// Acrescenta ao final os que ainda não existiam — mesma posição que
				// orderBy: createdAt asc traria num refetch
				const novos = circuitosDoServico.filter(
					(circuito) => !idsAtuais.includes(circuito?.id),
				);

				newForms[serviceId] = {
					...newForms[serviceId],
					clientServiceId: serviceId,
					circuits: [...circuitsAtualizados, ...novos],
				};
			});

			return newForms;
		});
	}, []);

	// Chama o service real de aplicação em massa (Task 3) e reidrata forms com o retorno do
	// backend (Task 5, Step 2) — transação única no backend: ou aplica em todos os alvos, ou em
	// nenhum. Não trata erro aqui de propósito: quem chama (ModalTemplates) decide o snackbar/estado
	// do modal a partir do reject, seguindo o erro tudo-ou-nada (Step 4 do brief)
	const handleAplicarTemplateEmMassa = useCallback(
		async ({ templateId, circuitoIds, novosCircuitos }) => {
			const resultado = await applyManyDiagramaTemplate({
				templateId,
				circuitoIds,
				novosCircuitos,
			});
			reidratarCircuitosAplicados(resultado?.aplicados);
			return resultado;
		},
		[reidratarCircuitosAplicados],
	);

	// Valor do AplicarEmMassaContext: `changed` é o mesmo estado global da aba (I11) que já
	// alimenta useBlocker/beforeunload/ClickAwayListener acima, aqui vira o gate de aplicação
	const aplicarEmMassaContextValue = useMemo(
		() => ({
			servicos: servicosParaAplicarEmMassa,
			bloqueadoPorPendencia: changed,
			aplicarTemplateEmMassa: handleAplicarTemplateEmMassa,
			reapplySignal,
		}),
		[servicosParaAplicarEmMassa, changed, handleAplicarTemplateEmMassa, reapplySignal],
	);



	// Navega para um circuito (troca de serviço e/ou de circuito), guardada pelo mesmo modal de saída usado na rota/Drawer (I11)
	const irParaCircuito = (servico, circuitKey) => {
		const serviceId = servico?.id_cliente_servico;
		if (selectCliente === serviceId && selectedCircuitKey === circuitKey) return;

		setSelectedTitle(`${serviceId} - ${servico?.nome}`);

		const aplicar = () => {
			setSelectCliente(serviceId);
			setSelectedCircuitKey(circuitKey);
			if (windowWidth < 750) toggleDrawer(true);
		};

		if (changed) {
			pendingNavigation.current = { type: "accordion", to: { serviceId, circuitKey } };
			setConfirmacaoSaidaPagina(true);
		} else {
			aplicar();
		}
	};

	// Cria um circuito vazio no serviço indicado e o seleciona. Por padrão força edição — ação
	// explícita do usuário (menu "Adicionar circuito"/botão "+"), nunca cai em preview de um
	// circuito que ainda não existe de verdade (sem id, só ganha ao salvar). Exceção:
	// `forceEditMode: false`, usado pelo auto-criação silenciosa de serviceItem.js quando o
	// serviço selecionado ainda não tem nenhum circuito — aí abre em preview (vazio), sem marcar
	// `changed`, pra não travar a navegação com o guard de saída por algo que o usuário não fez.
	const handleAdicionarCircuito = (serviceId, serviceNome, { forceEditMode = true } = {}) => {
		const existingSerial = forms?.[serviceId]?.circuits?.find((c) => c?.serial)?.serial;
		const novoCircuito = {
			clientServiceId: serviceId,
			tempId: uuidv4(),
			...(existingSerial ? { serial: existingSerial } : {}),
		};

		const aplicar = () => {
			setForms((prev) => ({
				...prev,
				[serviceId]: {
					...prev?.[serviceId],
					clientServiceId: serviceId,
					circuits: [...(prev?.[serviceId]?.circuits || []), novoCircuito],
				},
			}));
			if (serviceNome) setSelectedTitle(`${serviceId} - ${serviceNome}`);
			setSelectCliente(serviceId);
			setSelectedCircuitKey(novoCircuito.tempId);
			if (forceEditMode) {
				setEditMode(true);
				setChanged(true);
			}
			if (windowWidth < 750) toggleDrawer(true);
		};

		if (changed) {
			pendingNavigation.current = { type: "add-circuito", to: { serviceId, serviceNome, novoCircuito } };
			setConfirmacaoSaidaPagina(true);
		} else {
			aplicar();
		}
	};

	// Remove um circuito sem mutar o array (updater puro: StrictMode roda 2x — I2); só é acionado no serviço aberto, então usa selectCliente direto
	function removerCircuitoDaLista(index) {
		const circuitoRemovidoKey =
			forms?.[selectCliente]?.circuits?.[index]?.id ||
			forms?.[selectCliente]?.circuits?.[index]?.tempId;
		const eraOSelecionado =
			circuitoRemovidoKey != null && circuitoRemovidoKey === selectedCircuitKey;

		setForms((prev) => ({
			...prev,
			[selectCliente]: {
				...prev?.[selectCliente],
				circuits: (prev?.[selectCliente]?.circuits || []).filter(
					(_, i) => i !== index,
				),
			},
		}));

		// Se o circuito removido era o selecionado, seleciona quem ocupa a mesma posição na lista restante
		if (eraOSelecionado) {
			const restantes = (forms?.[selectCliente]?.circuits || []).filter(
				(_, i) => i !== index,
			);
			const proximo =
				restantes[Math.min(index, Math.max(restantes.length - 1, 0))];
			setSelectedCircuitKey(proximo ? proximo?.id || proximo?.tempId : null);
		}
	}

	// Aciona remoção direta (circuito novo, sem id) ou pede confirmação (já persistido)
	function handleCloseClick(index) {
		if (!forms?.[selectCliente]?.circuits?.[index]?.id) {
			removerCircuitoDaLista(index);
		} else {
			setDeletingCircuitIndex(index);
			setConfirmRemoveCircuit(true);
		}
		setChanged(true);
	}

	async function deleteReceivedTab(index) {
		try {
			setLoadingRemoveCircuit(true);
			await api.post("/interno/delete/circuitForm", {
				id: forms?.[selectCliente]?.circuits?.[index]?.id,
			});

			removerCircuitoDaLista(index);
			setConfirmRemoveCircuit(false);
		} catch (error) {
			console.error(error);
		} finally {
			setLoadingRemoveCircuit(false);
			setDeletingCircuitIndex(null);
		}
	}

	// Ids de serviços expandidos (padrão visual 9.0-clientesSwap: serviço = "trecho", circuito = "subtrecho")
	const [expandedIds, setExpandedIds] = useState([]);

	// Limpa a expansão ao trocar de cliente, mesmo reset dos irmãos selectCliente/selectedCircuitKey acima
	useEffect(() => {
		setExpandedIds([]);
	}, [clienteInfo]);

	// Expande automaticamente o serviço selecionado (clicar no serviço já mostra o circuito
	// escolhido na lista, sem precisar clicar no chevron à parte); ainda pode ser recolhido
	// manualmente depois — não força, só garante que comece expandido ao selecionar
	useEffect(() => {
		if (!selectCliente) return;
		setExpandedIds((prev) =>
			prev.includes(selectCliente) ? prev : [...prev, selectCliente],
		);
	}, [selectCliente]);

	const handleToggleExpand = (id, e) => {
		e.stopPropagation();
		e.preventDefault();
		setExpandedIds((prev) =>
			prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
		);
	};

	// Serviços com ao menos um circuito, calculado sobre a lista filtrada (não só a página atual) para que Expandir/Recolher Todos valha entre páginas
	const parentsWithChildrenIds = (filteredServices || [])
		.filter((servico) => (forms?.[servico?.id_cliente_servico]?.circuits?.length || 0) > 0)
		.map((servico) => servico?.id_cliente_servico);

	const allExpanded =
		parentsWithChildrenIds.length > 0 &&
		parentsWithChildrenIds.every((id) => expandedIds.includes(id));

	const handleToggleAll = () => {
		if (allExpanded) {
			setExpandedIds([]);
		} else {
			setExpandedIds(parentsWithChildrenIds);
		}
	};

	return (
		<AplicarEmMassaContext.Provider value={aplicarEmMassaContextValue}>
		<Box sx={styles.paper}>
			{/** Mostrar/Ocultar cancelados */}
			{!loading && services?.length > 0 && (
				<Box sx={styles.boxSwitch}>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch
									sx={styles.switch}
									checked={showCanceled}
									onChange={(e) => setShowCanceled(e.target.checked)}
									checkedIcon={
										<CheckCircle sx={{ fontSize: 22, color: "#fff" }} />
									}
									icon={<Cancel sx={{ fontSize: 22, color: "#fff" }} />}
									focusVisibleClassName=".Mui-focusVisible"
									disableRipple
								/>
							}
							label={
								<Typography
									variant="body2"
									sx={{
										fontWeight: 600,
										ml: 1,
										color: showCanceled ? "text.primary" : "text.secondary",
										fontSize: "0.85rem",
										userSelect: "none",
									}}
								>
									{showCanceled ? "Ocultar Cancelados" : "Mostrar Cancelados"}
								</Typography>
							}
							sx={{ margin: 0 }}
						/>
					</FormGroup>
				</Box>
			)}
			{loading ? (
				<Grid item xs={12} mt={2}>
					<LoaderThreeDots />
				</Grid>
			) : (
				<Grid
					container
					spacing={1}
					columns={{
						xs: 8,
						sm: 8,
						smx: 8,
						md: 12,
						lg: 12,
						xl: 12,
						xxl: 12,
					}}
				>
					<Grid sx={styles.paperFluxo} item xs={8} sm={8} smx={3} md={4} lg={4}>
						<Paper
							elevation={theme.palette.mode === "dark" ? 0 : 3}
							sx={{ ...styles.paperFluxo, height: "83vh" }}
						>
							{/* Expandir/Recolher todos os serviços com circuitos, mesmo padrão do 9.0-clientesSwap */}
							{parentsWithChildrenIds.length > 0 && (
								<Box sx={styles.boxExpandAll}>
									<Button
										size="small"
										onClick={handleToggleAll}
										startIcon={allExpanded ? <UnfoldLess /> : <UnfoldMore />}
										variant="outlined"
									>
										{allExpanded ? "Recolher Todos" : "Expandir Todos"}
									</Button>
								</Box>
							)}
							{currentServices?.map((servico) => {
								const isSelected =
									selectCliente === servico?.id_cliente_servico;

								const serviceForms = forms?.[servico?.id_cliente_servico]?.circuits;
								// Verifica se algum circuito manual (não ERP) está configurado
								const isConfigured = serviceForms?.some((circuit) => {
									if (circuit?.syncedByErp) return false;
									return (
										!!circuit?.serial ||
										!!circuit?.blockIP ||
										!!circuit?.ipPublico ||
										!!circuit?.vlanId ||
										!!circuit?.companySideIPv4 ||
										!!circuit?.companySideIPv6 ||
										!!circuit?.clientIPv4 ||
										!!circuit?.clientIPv6 ||
										!!circuit?.observations ||
										!!circuit?.lastMilePartner?.partnerName ||
										!!circuit?.lastMilePartner?.partnerContact ||
										!!circuit?.endClient?.endClientName ||
										(circuit?.lastMilePartner?.partnerEnabled !== null && circuit?.lastMilePartner?.partnerEnabled !== undefined) ||
										(circuit?.endClient?.endClientEnabled !== null && circuit?.endClient?.endClientEnabled !== undefined) ||
										(circuit?.speed?.download !== null && circuit?.speed?.download !== undefined) ||
										(circuit?.speed?.upload !== null && circuit?.speed?.upload !== undefined)
									);
								}) || false;

								// Subitens de circuito do serviço (Task 6): rótulo + indicadores próprios (formulário/anexos/diagrama),
								// todos computados do que já está em `forms` — sem fetch extra, pra aparecer sem precisar abrir o circuito.
								// anexosCount vem pronto do backend (findCircuitForms faz um groupBy avulso; ver CLAUDE.md do backend).
								const circuitItems = (serviceForms || []).map((circuit) => ({
									circuit,
									isConfigured: isCircuitoConfigurado(circuit),
									anexosCount: circuit?.anexosCount || 0,
									temDiagrama: (circuit?.diagramaEquipamentos?.nodes?.length || 0) > 0,
								}));

								// Expandir/recolher (adaptação visual do padrão 9.0-clientesSwap): só serviços com circuitos ganham o botão
								const hasChildren = circuitItems.length > 0;
								const isExpanded = expandedIds.includes(servico?.id_cliente_servico);

								return (
									<CardSelection
										key={servico?.id_cliente_servico}
										idCliente={servico?.id_cliente_servico}
										service={servico?.nome}
										status={servico?.status}
										selected={isSelected}
										isConfigured={isConfigured}
										ownerNome={servico?.ownerNome}
										circuits={circuitItems}
										selectedCircuitKey={selectedCircuitKey}
										editMode={editMode}
										hasChildren={hasChildren}
										isExpanded={isExpanded}
										onToggleExpand={(e) => handleToggleExpand(servico?.id_cliente_servico, e)}
										onSelectCircuit={(circuit) =>
											irParaCircuito(servico, getCircuitKey(circuit))
										}
										onRemoveCircuit={(index) => handleCloseClick(index)}
										onAdicionarCircuito={() =>
											handleAdicionarCircuito(servico?.id_cliente_servico, servico?.nome)
										}
										onClick={() => {
											// 1) Se já está selecionado, não faz nada (troca de circuito é feita pelo subitem)
											if (isSelected) return;

											const primeiroCircuito = serviceForms?.[0]
												? getCircuitKey(serviceForms[0])
												: null;
											irParaCircuito(servico, primeiroCircuito);
										}}
									/>
								);
							})}

							{totalPages > 1 && (
								<Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
									<Pagination
										count={totalPages}
										page={currentPage}
										onChange={handlePageChange}
										color="primary"
										size="small"
									/>
								</Box>
							)}
						</Paper>
					</Grid>

					{windowWidth >= 750 && (
						<Grid
							sx={styles.paperFluxo}
							item
							xs={8}
							sm={8}
							smx={5}
							md={8}
							lg={8}
						>
							{selectCliente && <Box sx={styles.boxBlur} />}
							<ClickAwayListener
								onClickAway={(event) => {
									if (!changed || !!confirmInputClick) return;

									const targetInput = event.target.closest(
										".MuiInputBase-root, .MuiAutocomplete-root, .MuiSelect-root",
									);

									if (targetInput) {
										event.preventDefault();
										event.stopPropagation();

										pendingInputClickRef.current = () => targetInput.click();
										setConfirmInputClick(true);
									}
								}}
							>
								<Paper sx={{ ...styles.paperFluxo, height: "83vh", pt: 0 }}>
									{selectCliente ? (
										<ServiceItem
											key={selectCliente}
											servico={selectCliente}
											ownerClienteId={selectedOwnerId}
											onFormUpdate={updateForm}
											otherOptions={otherOptions}
											popTags={popTags}
											regionalTags={regionalTags}
											loadingTags={loadingTags}
											loading={loading}
											setLoading={setLoading}
											updateOptions={getDeliveryOptions}
											optionsRoteamento={optionsRoteamento}
											optionsEntregaFinal={optionsEntregaFinal}
											optionsEntregaFinalCliente={optionsEntregaFinalCliente}
											setOptionsEntregaFinal={setOptionsEntregaFinal}
											setOptionsRoteamento={setOptionsRoteamento}
											setOptionsEntregaFinalCliente={
												setOptionsEntregaFinalCliente
											}
											setChanged={setChanged}
											changed={changed}
											setForms={setForms}
											forms={forms}
											editMode={editMode}
											setEditMode={setEditMode}
											selectedTitle={selectedTitle}
											setSnackbar={setSnackbar}
											clientCpes={clientCpes}
											clientHosts={clientHosts}
											selectedCircuitKey={selectedCircuitKey}
											onAdicionarCircuito={handleAdicionarCircuito}
											confirmRemoveCircuit={confirmRemoveCircuit}
											loadingRemoveCircuit={loadingRemoveCircuit}
											onConfirmRemove={() => deleteReceivedTab(deletingCircuitIndex)}
											onCancelRemove={setConfirmRemoveCircuit}
											onCircuitoLoadingChange={setCircuitoSalvando}
										/>
									) : (
										<InformationMale
											mainFill={theme.palette.secondary.dark}
											primaryCircleFill={theme.palette.secondary.main}
											secondaryCircleFill={theme.palette.secondary.light}
											title={"Você ainda não selecionou um"}
											highlight={"circuito"}
											subtitle={
												"As informações de circuito aparecerão aqui assim\nque um cliente for escolhido."
											}
										/>
									)}
								</Paper>
							</ClickAwayListener>
						</Grid>
					)}
				</Grid>
			)}

			{/* Modo mobile */}
			<Drawer
				anchor="right"
				open={openDrawer}
				onClose={() => {
					// 2) Se tentar fechar o Drawer em edição/alterações, pedir confirmação
					if (editMode || changed) {
						pendingNavigation.current = { type: "close-drawer" };
						setConfirmacaoSaidaPagina(true);
					} else {
						toggleDrawer(false);
					}
				}}
				sx={{ position: "relative" }}
			>
				{selectCliente && (
					<Box
						sx={{
							...styles.boxBlur,
							width: "100%",
							transform: "none",
							borderRadius: 0,
						}}
					/>
				)}
				<Box sx={{ ...styles.paperFluxo, width: "100vw", pt: 0, mb: 2 }}>
					<IconButton
						onClick={() => {
							toggleDrawer(false);
							setSelectCliente(null);
							setSelectedCircuitKey(null);
						}}
						sx={{ mb: -1, zIndex: 99 }}
					>
						<CloseOutlined />
					</IconButton>
					{selectCliente ? (
						<ServiceItem
							key={selectCliente}
							servico={selectCliente}
							ownerClienteId={selectedOwnerId}
							onFormUpdate={updateForm}
							otherOptions={otherOptions}
							popTags={popTags}
							regionalTags={regionalTags}
							loadingTags={loadingTags}
							loading={loading}
							setLoading={setLoading}
							updateOptions={getDeliveryOptions}
							optionsRoteamento={optionsRoteamento}
							optionsEntregaFinal={optionsEntregaFinal}
							optionsEntregaFinalCliente={optionsEntregaFinalCliente}
							setOptionsEntregaFinal={setOptionsEntregaFinal}
							setOptionsRoteamento={setOptionsRoteamento}
							setOptionsEntregaFinalCliente={setOptionsEntregaFinalCliente}
							setChanged={setChanged}
							changed={changed}
							setForms={setForms}
							forms={forms}
							editMode={editMode}
							setEditMode={setEditMode}
							selectedTitle={selectedTitle}
							setSnackbar={setSnackbar}
							clientCpes={clientCpes}
							clientHosts={clientHosts}
							selectedCircuitKey={selectedCircuitKey}
							onAdicionarCircuito={handleAdicionarCircuito}
							confirmRemoveCircuit={confirmRemoveCircuit}
							loadingRemoveCircuit={loadingRemoveCircuit}
							onConfirmRemove={() => deleteReceivedTab(deletingCircuitIndex)}
							onCancelRemove={setConfirmRemoveCircuit}
							onCircuitoLoadingChange={setCircuitoSalvando}
						/>
					) : (
						<InformationMale
							mainFill={theme.palette.secondary.dark}
							primaryCircleFill={theme.palette.secondary.main}
							secondaryCircleFill={theme.palette.secondary.light}
							title={"Você ainda não selecionou um"}
							highlight={"circuito"}
							subtitle={
								"As informações de circuito aparecerão aqui assim\nque um cliente for escolhido."
							}
						/>
					)}
				</Box>
			</Drawer>

			{/* Modal de aviso de saída */}
			<InternoModalConfirmacaoSaida
				openConfirmacao={confirmacaoSaidaPagina}
				confirmLoad={false}
				// Bloqueia o Continuar enquanto o circuito aberto tem um save em voo (fix Task 7)
				confirmDisabled={circuitoSalvando}
				setOpenConfirmacao={setConfirmacaoSaidaPagina}
				execute={handleConfirmNavigation}
				cancel={handleCancelNavigation}
				severity={"warning"}
				title={"Atenção"}
				contentText={
					"As alterações não salvas serão perdidas, deseja continuar?"
				}
				headerText={"Você está prestes a sair do modo de edição."}
				confirmText={"Continuar"}
			/>
			<InternoModalConfirmacaoSaida
				openConfirmacao={confirmInputClick}
				confirmLoad={false}
				setOpenConfirmacao={setConfirmInputClick}
				execute={handleConfirmPendingClick}
				cancel={handleCancelPendingClick}
				severity={"warning"}
				contentText={"Alterações não salvas serão perdidas, deseja continuar?"}
				title={"Atenção"}
				headerText={"Você está saindo do modo de edição."}
				confirmText={"Continuar"}
			/>
		</Box>
		</AplicarEmMassaContext.Provider>
	);
}
